import React, { useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { 
  Zap, 
  Shield, 
  CheckCircle, 
  Sparkles, 
  Mail, 
  Lock, 
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  User
} from "lucide-react";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';
const APP_NAME = "VALIDMAIL";
const SUPPORT_EMAIL = "support@validmail.io";

export default function SignUp({ setAuthToken, setUser, setStatusMessage, pricingCurrency }) {
  // Google auth state
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  // Signup state
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupOtp, setSignupOtp] = useState("");
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  
  // Step state
  const [step, setStep] = useState(1); // 1 = form, 2 = OTP verification
  const [signupLoading, setSignupLoading] = useState(false);

  // ==================== GOOGLE SIGNUP ====================
  const handleGoogleSignupSuccess = async (credentialResponse) => {
    if (!credentialResponse?.credential) {
      setAuthError("Unable to retrieve Google credential.");
      return;
    }
    setAuthLoading(true);
    setAuthError("");
    try {
      const response = await axios.post(`${API}/auth/social-login`, {
        provider: "google",
        token: credentialResponse.credential,
        currency_preference: pricingCurrency || "USD",
      });
      const { access_token: accessToken, user: userData, accessToken: alt } = response.data;
      const tokenToSave = accessToken || alt;
      if (tokenToSave) {
        localStorage.setItem("authToken", tokenToSave);
        setAuthToken?.(tokenToSave);
      }
      setUser?.(userData);
      setStatusMessage?.({ type: "success", text: `Welcome, ${userData?.name || "user"}!` });
      window.location.href = "/";
    } catch (error) {
      const message = error?.response?.data?.detail || "Google sign-up failed.";
      setAuthError(message);
      setStatusMessage?.({ type: "error", text: message });
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogleSignupError = () => {
    setAuthError("Google sign-up was cancelled.");
  };

  // ==================== STEP 1: SEND OTP ====================
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setAuthError("");
    
    if (!signupName || !signupEmail || !signupPassword) {
      setAuthError("Please fill in all fields");
      return;
    }

    if (signupPassword.length < 6) {
      setAuthError("Password must be at least 6 characters");
      return;
    }

    setSignupLoading(true);
    try {
      await axios.post(`${API}/auth/signup/send-otp`, { 
        email: signupEmail 
      });
      setStep(2);
      setStatusMessage?.({ type: "success", text: "OTP sent to your email!" });
    } catch (error) {
      const message = error?.response?.data?.detail || "Failed to send OTP.";
      setAuthError(message);
      setStatusMessage?.({ type: "error", text: message });
    } finally {
      setSignupLoading(false);
    }
  };

  // ==================== STEP 2: VERIFY OTP & SIGNUP ====================
  const handleVerifyAndSignup = async (e) => {
    e.preventDefault();
    setAuthError("");
    
    if (!signupOtp) {
      setAuthError("Please enter the OTP code");
      return;
    }

    setSignupLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup/verify`, {
        name: signupName,
        email: signupEmail,
        password: signupPassword,
        otp: signupOtp,
        currency_preference: pricingCurrency || "USD",
      });
      const { access_token: accessToken, user: userData } = response.data;
      if (accessToken) {
        localStorage.setItem("authToken", accessToken);
        setAuthToken?.(accessToken);
      }
      setUser?.(userData);
      setStatusMessage?.({ type: "success", text: `Welcome, ${userData?.name || "user"}!` });
      window.location.href = "/";
    } catch (error) {
      const message = error?.response?.data?.detail || "Invalid OTP or signup failed.";
      setAuthError(message);
      setStatusMessage?.({ type: "error", text: message });
    } finally {
      setSignupLoading(false);
    }
  };

  // ==================== HELPER: GO BACK ====================
  const goBackToForm = () => {
    setStep(1);
    setSignupOtp("");
    setAuthError("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-6xl w-full bg-white shadow-2xl rounded-2xl sm:rounded-3xl overflow-hidden grid grid-cols-1 lg:grid-cols-2 border border-gray-200">
        
        {/* LEFT - Company / Marketing */}
        <div className="p-6 sm:p-8 lg:p-12 bg-gradient-to-br from-purple-600 to-blue-600 text-white relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
          </div>

          <div className="relative z-10 flex flex-col h-full justify-between">
            <div>
              {/* Logo & Title */}
              <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
                <div className="bg-white/20 backdrop-blur p-2.5 sm:p-3 rounded-xl shadow-lg">
                  <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold">{APP_NAME}</h1>
                  <p className="text-xs sm:text-sm text-purple-100">Email Validation Platform</p>
                </div>
              </div>

              {/* Main Heading */}
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4 leading-tight">
                Start validating emails today
              </h2>
              <p className="text-sm sm:text-base text-purple-100 mb-6 sm:mb-8 leading-relaxed">
                Join thousands of developers using our AI-powered email validation platform.
              </p>

              {/* Features */}
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm sm:text-base">100 Free Credits</div>
                    <div className="text-xs sm:text-sm text-purple-100">Start validating immediately</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm sm:text-base">No Credit Card Required</div>
                    <div className="text-xs sm:text-sm text-purple-100">Get started in seconds</div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                    <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm sm:text-base">Full API Access</div>
                    <div className="text-xs sm:text-sm text-purple-100">Integrate with your apps</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 sm:mt-12">
              <div className="bg-white/10 backdrop-blur rounded-xl p-3 sm:p-4 border border-white/20">
                <p className="text-xs sm:text-sm text-purple-100">
                  🚀 Join developers from 100+ countries using {APP_NAME}
                </p>
              </div>
              <p className="mt-4 text-xs sm:text-sm text-purple-100">
                © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
              </p>
            </div>
          </div>
        </div>

        {/* RIGHT - Signup Form */}
        <div className="p-6 sm:p-8 lg:p-12 flex items-center justify-center">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="mb-6 sm:mb-8 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-purple-100 text-purple-700 text-xs sm:text-sm font-medium mb-4">
                <User className="w-3 h-3 sm:w-4 sm:h-4" />
                Create Account
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {step === 1 ? "Sign up for free" : "Verify your email"}
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                {step === 1 ? "Get started with 100 free credits" : "Enter the code we sent to your email"}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Already have an account?{" "}
                <Link to={"/signin"} className="text-purple-600 hover:text-purple-700 font-semibold">
                  Sign in
                </Link>
              </p>
            </div>

            {/* Auth Card */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-sm">
              {authError && (
                <div className="mb-4 sm:mb-6 text-sm sm:text-base text-red-700 bg-red-50 border-2 border-red-200 p-3 sm:p-4 rounded-lg flex items-start gap-2">
                  <span className="flex-shrink-0 text-lg">⚠️</span>
                  <span>{authError}</span>
                </div>
              )}

              {/* STEP 1: EMAIL & PASSWORD FORM */}
              {step === 1 && (
                <div className="space-y-4 sm:space-y-6">
                  <form onSubmit={handleSendOtp} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          value={signupName}
                          onChange={(e) => setSignupName(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                          placeholder="John Doe"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          value={signupEmail}
                          onChange={(e) => setSignupEmail(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                          placeholder="you@example.com"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password
                      </label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type={showSignupPassword ? "text" : "password"}
                          value={signupPassword}
                          onChange={(e) => setSignupPassword(e.target.value)}
                          className="w-full pl-10 pr-12 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                          placeholder="••••••••"
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignupPassword(!showSignupPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showSignupPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Minimum 6 characters</p>
                    </div>

                    <button
                      type="submit"
                      disabled={signupLoading}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {signupLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending OTP...
                        </>
                      ) : (
                        <>
                          Continue
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </form>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-xs sm:text-sm">
                      <span className="px-2 sm:px-4 bg-gray-50 text-gray-500">Or sign up with</span>
                    </div>
                  </div>

                  {/* Google Signup */}
                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={handleGoogleSignupSuccess}
                      onError={handleGoogleSignupError}
                      useOneTap={false}
                      size="large"
                      width="100%"
                    />
                  </div>

                  {authLoading && (
                    <div className="flex flex-col items-center justify-center py-4 gap-3">
                      <Loader2 className="animate-spin h-8 w-8 text-purple-600" />
                      <p className="text-sm text-gray-600 font-medium">Creating your account...</p>
                    </div>
                  )}
                </div>
              )}

              {/* STEP 2: OTP VERIFICATION */}
              {step === 2 && (
                <div className="space-y-4 sm:space-y-6">
                  <form onSubmit={handleVerifyAndSignup} className="space-y-4">
                    <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                      <p className="text-sm text-purple-700">
                        ✉️ OTP sent to <strong>{signupEmail}</strong>
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Enter Verification Code
                      </label>
                      <input
                        type="text"
                        value={signupOtp}
                        onChange={(e) => setSignupOtp(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors text-center text-2xl tracking-widest"
                        placeholder="000000"
                        maxLength={6}
                        required
                      />
                      <p className="mt-2 text-xs text-gray-500">
                        Check your email for the 6-digit code
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={signupLoading}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {signupLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Creating account...
                        </>
                      ) : (
                        <>
                          Verify & Create Account
                          <ArrowRight className="w-5 h-5" />
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={goBackToForm}
                      className="w-full text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      ← Change email or resend code
                    </button>
                  </form>
                </div>
              )}

              {/* Features */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-6">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>100 free credits</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>No card required</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>Instant setup</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span>API access</span>
                </div>
              </div>

              {/* Terms */}
              <div className="text-center text-xs sm:text-sm text-gray-500 mt-6">
                By continuing, you agree to our{" "}
                <a href="/terms" className="text-purple-600 hover:text-purple-700 font-medium">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="/privacy" className="text-purple-600 hover:text-purple-700 font-medium">
                  Privacy Policy
                </a>
                .
              </div>
            </div>

            {/* Help */}
            <div className="mt-6 sm:mt-8 text-center">
              <p className="text-xs sm:text-sm text-gray-600">
                Need help?{" "}
                <a 
                  href={`mailto:${SUPPORT_EMAIL}`} 
                  className="text-purple-600 hover:text-purple-700 font-medium inline-flex items-center gap-1"
                >
                  Contact Support <ArrowRight className="w-3 h-3" />
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
